import React, { useEffect, useState, useRef } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import { getCompanyLogoUrl } from '../utils/imageUtils';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function TdsReport() {
    const { http, user } = AuthUser();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [totals, setTotals] = useState({});
    const [companyName, setCompanyName] = useState('');
    const [companyLogo, setCompanyLogo] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const printRef = useRef(null);

    useEffect(() => {
        fetchReport();
        fetchCompanyInfo();
    }, []);

    const fetchCompanyInfo = async () => {
        try {
            const response = await http.get(`/companyinfo?login_user=${user.id}`);
            if (response.data.data && response.data.data.length > 0) {
                setCompanyName(response.data.data[0].company_name || '');
                setCompanyLogo(response.data.data[0].company_logo || '');
                setCompanyAddress(response.data.data[0].company_address || '');
            }
        } catch (error) {
            console.error('Error fetching company info:', error);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/salary_report/tds?login_user=${user.id}`);
            if (response.data.success) {
                setReportData(response.data.data);
                setTotals(response.data.totals || {});
            }
        } catch (error) {
            console.error('Error fetching TDS report:', error);
            toast.error('Error loading TDS report');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

    const hasDeductionFormula = reportData.some(r => parseFloat(r.tds_deduction) > 0);

    const baseHeaders = ['Sl.No', 'Name', 'Gross Salary', 'Basic Salary', 'TDS %', 'TDS Amount (D)'];
    const headers = hasDeductionFormula ? [...baseHeaders, 'TDS Deduction (Formula)'] : baseHeaders;

    const buildRows = () => reportData.map(r => {
        const row = [r.sl_no, r.name, r.gross_salary || 0, r.basic_salary || 0, r.tds_percent || 0, r.tds_amount || 0];
        if (hasDeductionFormula) row.push(r.tds_deduction || 0);
        return row;
    });

    const totalRow = () => {
        const row = ['', 'Total', totals.gross_salary || 0, '', '', totals.tds_amount || 0];
        if (hasDeductionFormula) row.push(totals.tds_deduction || 0);
        return row;
    };

    const styleSheet = (ws, colCount, rowCount) => {
        for (let c = 0; c < colCount; c++) {
            const addr = XLSX.utils.encode_cell({ r: 0, c });
            if (ws[addr]) ws[addr].s = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '1A237E' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: { top: { style: 'thin', color: { rgb: '1A237E' } }, bottom: { style: 'thin', color: { rgb: '1A237E' } }, left: { style: 'thin', color: { rgb: '1A237E' } }, right: { style: 'thin', color: { rgb: '1A237E' } } } };
        }
        for (let r = 1; r <= rowCount; r++) {
            const isTotal = r === rowCount;
            const isEven = r % 2 === 0;
            for (let c = 0; c < colCount; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                if (!ws[addr]) continue;
                const isNum = typeof ws[addr].v === 'number';
                ws[addr].s = {
                    font: { sz: 10, bold: isTotal, color: { rgb: isTotal ? '1A237E' : '333333' } },
                    fill: { fgColor: { rgb: isTotal ? 'E8EAF6' : isEven ? 'F0F0FF' : 'FFFFFF' } },
                    alignment: { horizontal: isNum ? 'right' : (c === 0 ? 'center' : 'left'), vertical: 'center' },
                    border: { top: { style: 'thin', color: { rgb: isTotal ? '1A237E' : 'DDDDDD' } }, bottom: { style: 'thin', color: { rgb: 'DDDDDD' } }, left: { style: 'thin', color: { rgb: 'DDDDDD' } }, right: { style: 'thin', color: { rgb: 'DDDDDD' } } },
                    ...(isNum ? { numFmt: '#,##0.00' } : {})
                };
            }
        }
    };

    const handleExcel = () => {
        if (reportData.length === 0) return;
        const rows = buildRows();
        const data = [headers, ...rows, totalRow()];
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = headers.map((h, i) => ({ wch: i === 1 ? 22 : 16 }));
        ws['!rows'] = [{ hpt: 22 }];
        styleSheet(ws, headers.length, rows.length + 1);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'TDS Report');
        XLSX.writeFile(wb, `TDS_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handlePdf = () => {
        if (reportData.length === 0) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(16); doc.setTextColor(26, 35, 126);
        doc.text(companyName || 'Company', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
        if (companyAddress) { doc.setFontSize(9); doc.setTextColor(100); doc.text(companyAddress, doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' }); }
        doc.setFontSize(12); doc.setTextColor(255); doc.setFillColor(26, 35, 126);
        doc.rect(14, 25, doc.internal.pageSize.getWidth() - 28, 8, 'F');
        doc.text('TAX DEDUCTED AT SOURCE (TDS) REPORT', doc.internal.pageSize.getWidth() / 2, 30.5, { align: 'center' });
        const rows = buildRows(); rows.push(totalRow());
        const colStyles = { 0: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'center' }, 5: { halign: 'right' } };
        if (hasDeductionFormula) colStyles[6] = { halign: 'right' };
        autoTable(doc, {
            head: [headers], body: rows, startY: 37, theme: 'grid',
            headStyles: { fillColor: [26, 35, 126], textColor: 255, fontSize: 8, halign: 'center' },
            bodyStyles: { fontSize: 8 }, columnStyles: colStyles,
            didParseCell: (data) => { if (data.row.index === rows.length - 1) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [232, 234, 246]; } }
        });
        doc.save(`TDS_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const handleCsvDownload = () => {
        if (reportData.length === 0) return;
        const rows = buildRows().map(r => [r[0], `"${(r[1] || '').toString().replace(/"/g, '""')}"`, ...r.slice(2)]);
        rows.push(totalRow());
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a'); link.href = url;
        link.setAttribute('download', `TDS_Report_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html><html><head><title>TDS Report - ${companyName}</title><style>
            body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#000}
            .report-header{text-align:center;border-bottom:3px solid #1a237e;padding-bottom:15px;margin-bottom:20px}
            table{width:100%;border-collapse:collapse}
            th{background:#1a237e;color:#fff;padding:8px 10px;font-size:12px;text-align:center;border:1px solid #1a237e}
            td{padding:6px 10px;font-size:11px;border:1px solid #ddd}
            .text-end{text-align:right}.text-center{text-align:center}
            @media print{@page{margin:10mm;size:A4}}
        </style></head><body>${printContent.innerHTML}</body></html>`);
        win.document.close(); win.focus(); win.onafterprint = () => win.close(); win.print();
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0"><i className="fa fa-file-alt me-2"></i>TDS Report</h5>
                    <div className="d-flex gap-1 align-items-center">
                        <button className="btn btn-sm btn-outline-success" onClick={handleExcel} disabled={reportData.length === 0} title="Download Excel">
                            <i className="fa fa-file-excel me-1"></i>Excel
                        </button>
                        <button className="btn btn-sm btn-outline-info" onClick={handleCsvDownload} disabled={reportData.length === 0} title="Download CSV">
                            <i className="fa fa-file-csv me-1"></i>CSV
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={handlePdf} disabled={reportData.length === 0} title="Download PDF">
                            <i className="fa fa-file-pdf me-1"></i>PDF
                        </button>
                        <button className="btn btn-sm btn-outline-dark" onClick={handlePrint} title="Print Report">
                            <i className="fa fa-print me-1"></i>Print
                        </button>
                        <span className="badge bg-secondary ms-1">{reportData.length} employees</span>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : reportData.length === 0 ? (
                            <div className="text-center py-4 text-muted"><i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i><p className="mb-0">No TDS records found</p></div>
                        ) : (
                            <div ref={printRef}>
                                <div className="report-header d-none" style={{ textAlign: 'center', borderBottom: '3px solid #1a237e', paddingBottom: '15px', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                                        {companyLogo && <img src={getCompanyLogoUrl(companyLogo)} alt="Logo" style={{ width: '60px', height: '50px', objectFit: 'contain' }} />}
                                        <div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1a237e', textTransform: 'uppercase' }}>{companyName}</div>
                                            {companyAddress && <div style={{ fontSize: '12px', color: '#555' }}>{companyAddress}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-none" style={{ background: '#1a237e', color: '#fff', textAlign: 'center', padding: '8px', fontSize: '16px', fontWeight: 'bold', letterSpacing: '2px', marginBottom: '10px' }}>
                                    TAX DEDUCTED AT SOURCE (TDS) REPORT
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm table-bordered mb-0">
                                        <thead style={{ backgroundColor: '#1a237e' }}>
                                            <tr>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'center', width: '50px' }}>Sl.No</th>
                                                <th style={{ color: '#fff', fontSize: '12px' }}>Name</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>Gross Salary</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>Basic Salary</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'center' }}>TDS %</th>
                                                <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>TDS Amount (D)</th>
                                                {hasDeductionFormula && <th style={{ color: '#fff', fontSize: '12px', textAlign: 'right' }}>TDS Deduction (Formula)</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.map((row) => (
                                                <tr key={row.sl_no}>
                                                    <td className="text-center" style={{ fontSize: '12px' }}>{row.sl_no}</td>
                                                    <td style={{ fontSize: '12px' }}><strong>{row.name}</strong></td>
                                                    <td className="text-end" style={{ fontSize: '12px' }}>{fmt(row.gross_salary)}</td>
                                                    <td className="text-end" style={{ fontSize: '12px' }}>{fmt(row.basic_salary)}</td>
                                                    <td className="text-center" style={{ fontSize: '12px' }}>{row.tds_percent}%</td>
                                                    <td className="text-end" style={{ fontSize: '12px', color: '#c62828', fontWeight: 'bold' }}>{fmt(row.tds_amount)}</td>
                                                    {hasDeductionFormula && <td className="text-end" style={{ fontSize: '12px', color: '#c62828' }}>{fmt(row.tds_deduction)}</td>}
                                                </tr>
                                            ))}
                                            <tr style={{ fontWeight: 'bold', background: '#e8eaf6', borderTop: '2px solid #1a237e' }}>
                                                <td colSpan="2" className="text-end" style={{ fontSize: '13px' }}>Total</td>
                                                <td className="text-end" style={{ fontSize: '13px' }}>{fmt(totals.gross_salary)}</td>
                                                <td></td><td></td>
                                                <td className="text-end" style={{ fontSize: '13px', color: '#c62828' }}>{fmt(totals.tds_amount)}</td>
                                                {hasDeductionFormula && <td className="text-end" style={{ fontSize: '13px', color: '#c62828' }}>{fmt(totals.tds_deduction)}</td>}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
